import MaskedView from "@react-native-masked-view/masked-view";
import { LinearGradient } from "expo-linear-gradient";
import type { PropsWithChildren } from "react";
import { Platform, StyleSheet, View, type ViewStyle } from "react-native";

/**
 * Hides scrolling foreground beneath a fixed header while leaving the single
 * page backdrop visible. No platform paints a second artwork copy for its
 * header, so header and page cannot crossfade a frame apart.
 */
export function HeaderContentMask({
  children,
  disabled = false,
  height
}: PropsWithChildren<{ disabled?: boolean; height: number }>) {
  // Fullscreen keeps the existing player mounted in this exact tree. Disable
  // the scrolling-header mask while that tree is stretched to the viewport;
  // a second fullscreen player would interrupt playback and native routes.
  if (Platform.OS === "web") {
    const transparentStop = Math.max(1, Math.round(height * 0.05));
    const maskImage = `linear-gradient(to bottom, transparent 0px, transparent ${transparentStop}px, black ${height}px, black 100%)`;
    return (
      <View
        style={[
          styles.contentMask,
          !disabled &&
            ({
              maskImage,
              WebkitMaskImage: maskImage
            } as ViewStyle)
        ]}
      >
        {children}
      </View>
    );
  }

  // Android keeps the scrolling scene on the ordinary hardware canvas. Its
  // fixed HeaderBackdropScrim covers and fades content beneath the header;
  // wrapping the full route in MaskedView makes native-stack Back composite a
  // page-sized offscreen surface.
  if (Platform.OS === "android") {
    return <View style={styles.contentMask}>{children}</View>;
  }

  return (
    <MaskedView
      style={styles.contentMask}
      maskElement={
        <View style={styles.maskShape}>
          <LinearGradient
            colors={
              disabled
                ? ["black", "black", "black"]
                : ["transparent", "transparent", "black"]
            }
            locations={[0, 0.05, 1]}
            style={{ height }}
          />
          <View style={styles.maskOpaque} />
        </View>
      }
    >
      {children}
    </MaskedView>
  );
}

const styles = StyleSheet.create({
  contentMask: {
    flex: 1,
    minHeight: 0
  },
  maskOpaque: {
    backgroundColor: "black",
    flex: 1
  },
  maskShape: {
    flex: 1,
    minHeight: 0
  }
});
